import { useContext, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { observer } from 'mobx-react-lite';
import { AppBar, Button, Divider, Menu, MenuItem, Stack, Toolbar } from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import PersonIcon from '@mui/icons-material/Person';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import LightModeIcon from '@mui/icons-material/LightMode';
import LogoutIcon from '@mui/icons-material/Logout';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts';

import { AuthStoreContext, ThemeStoreContext } from './Root';
import StyledButton from './styled/StyledButton';
import { SideBarButton, SideBarProps, SideBarState } from './SideBar';
import FullLogo from './images/FullLogo';

//=================================================================================================
function Header({ sideBar = { state: SideBarState.none, setState: () => {} } }: { sideBar?: SideBarProps }) {
    const { authStore } = useContext(AuthStoreContext);
    const { themeStore } = useContext(ThemeStoreContext);
    const navigate = useNavigate();

    const [mainMenuAnchor, setMainMenuAnchor] = useState<null | HTMLElement>(null);

    if (!authStore.user) return null;

    //-------------------------------------------------------------------------------------------------

    /**
     * Функция, генерирующая функцию-обработчик события onClick для элементов меню.
     * @param setAnchorFunction функция, изменяющая состояние якоря меню
     * @returns функция, которая будет вызвана при клике на пункт меню.
     */
    const createMenuButtonClickFunction = (
        setAnchorFunction: (anchor: React.SetStateAction<HTMLElement | null>) => void,
    ) => {
        return (direction: string) => {
            return () => {
                setAnchorFunction(null);
                navigate(direction);
            };
        };
    };

    //-------------------------------------------------------------------------------------------------
    const renderMainMenu = () => {
        if (!authStore.user) return null;

        const onMenuButtonClick = createMenuButtonClickFunction(setMainMenuAnchor);

        const renderAdminSettingsButton = () => {
            return authStore.user?.isAdmin() ? (
                <MenuItem key={1} onClick={onMenuButtonClick('/admin')}>
                    <AdminPanelSettingsIcon fontSize="small" sx={{ mr: 1 }} />
                    Настройки администратора
                </MenuItem>
            ) : null;
        };

        return (
            <Menu
                anchorEl={mainMenuAnchor}
                open={Boolean(mainMenuAnchor)}
                onClose={() => setMainMenuAnchor(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                slotProps={{ paper: { sx: { bgcolor: 'background.default' } } }}
            >
                <Divider />
                <MenuItem key={0} onClick={onMenuButtonClick('/user')}>
                    <ManageAccountsIcon fontSize="small" sx={{ mr: 1 }} />
                    Настройки пользователя
                </MenuItem>
                {renderAdminSettingsButton()}
                <MenuItem
                    key={2}
                    onClick={() => {
                        setMainMenuAnchor(null);
                        themeStore.toggle();
                    }}
                >
                    {themeStore.mode === 'light' ? (
                        <>
                            <DarkModeIcon fontSize="small" sx={{ mr: 1 }} />
                            Тёмная тема
                        </>
                    ) : (
                        <>
                            <LightModeIcon fontSize="small" sx={{ mr: 1 }} />
                            Светлая тема
                        </>
                    )}
                </MenuItem>
                <Divider />
                <MenuItem
                    key={3}
                    onClick={() => {
                        setMainMenuAnchor(null);
                        authStore.logout();
                    }}
                >
                    <LogoutIcon fontSize="small" sx={{ mr: 1 }} />
                    Выход
                </MenuItem>
            </Menu>
        );
    };

    //-------------------------------------------------------------------------------------------------
    return (
        <AppBar
            position="static"
            color="inherit"
            elevation={0}
            sx={{ borderBottom: '1px solid', borderColor: 'divider', bgcolor: 'background.default' }}
        >
            <Stack direction="row" justifyContent="space-between">
                <Stack direction="row">
                    <SideBarButton state={sideBar.state} setState={sideBar.setState} />
                    <Button onClick={() => navigate('/')}>
                        <FullLogo sx={{ fontSize: '5.2em', height: '100%', p: '0px 20px 0px 5px' }} />
                    </Button>
                </Stack>
                <Toolbar variant="dense">
                    <StyledButton onClick={(e) => setMainMenuAnchor(e.currentTarget)}>
                        <PersonIcon sx={{ mr: 1 }} />
                        {authStore.user.login}
                        <KeyboardArrowDownIcon sx={{ height: 16 }} />
                    </StyledButton>
                    {renderMainMenu()}
                </Toolbar>
            </Stack>
        </AppBar>
    );
}

export default observer(Header);
